import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import defaultConfig from '@crema/constants/defaultConfig';
import { useThemeActionsContext, useThemeContext } from './ThemeContextProvider';
import { LayoutDirection } from '@crema/constants/AppEnums';
import { LanguageProps } from '@crema/models/Apps';

import languageData from 'libs/components/src/lib/AppLngSwitcher/data';
import { getLocalStorage, LOCAL_STORAGE_NAMES } from '@libs/storage/src/lib/local';

export interface LocaleContextData {
  locale: LanguageProps;
  rtlLocale: string[];
}

export interface LocaleActionsData {
  updateLocale: (locale: LanguageProps) => void;
}

export const LocaleContext = createContext<LocaleContextData>({
  locale: defaultConfig.locale,
  rtlLocale: defaultConfig.rtlLocale,
});
export const LocaleActionsContext = createContext<LocaleActionsData>({
  updateLocale: () => {},
});

export const useLocaleContext = () => useContext(LocaleContext);

export const useLocaleActionsContext = () => useContext(LocaleActionsContext);

interface LocaleContextProviderProps {
  children: ReactNode;
}

const LocaleContextProvider: React.FC<LocaleContextProviderProps> = ({ children }) => {
  const currentLang = getLocalStorage(LOCAL_STORAGE_NAMES.LANG);

  const filterLang = languageData.find((item) => item.locale === currentLang?.toLowerCase());

  const [locale, updateLocale] = useState<LanguageProps>(defaultConfig.locale);
  const { theme } = useThemeContext();
  const { updateTheme } = useThemeActionsContext();

  useEffect(() => {
    if (defaultConfig.rtlLocale.includes(locale.locale) && theme.direction === LayoutDirection.LTR) {
      updateTheme({
        ...theme,
        direction: LayoutDirection.RTL,
      });
    } else if (!defaultConfig.rtlLocale.includes(locale.locale) && theme.direction === LayoutDirection.RTL) {
      updateTheme({
        ...theme,
        direction: LayoutDirection.LTR,
      });
    }
  }, [locale, theme, updateTheme]);

  // filterLang is re-read from localStorage every render; it is an element of languageData (stable ref) or undefined.
  const localeValue = useMemo(
    () => ({
      locale: filterLang ? filterLang : locale,
      rtlLocale: defaultConfig.rtlLocale,
    }),
    [filterLang, locale],
  );

  const localeActions = useMemo(() => ({ updateLocale }), []);

  return (
    <LocaleContext.Provider value={localeValue}>
      <LocaleActionsContext.Provider value={localeActions}>
        {children}
      </LocaleActionsContext.Provider>
    </LocaleContext.Provider>
  );
};

export default LocaleContextProvider;
